/**
 * ParticipantTile - Displays a single participant's video
 * Shows video feed, name, connection quality, and mute indicators
 */

import * as React from "react";
import { Participant, ParticipantEvent, Track } from "livekit-client";
import { cn } from "../../lib/utils";
import { MicOff } from "lucide-react";

export interface ParticipantTileProps {
  participant: Participant;
  isSpotlight?: boolean;
  showName?: boolean;
  className?: string;
}

export function ParticipantTile({
  participant,
  isSpotlight = false,
  showName = true,
  className,
}: ParticipantTileProps) {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const audioRef = React.useRef<HTMLAudioElement>(null);
  const [isMuted, setIsMuted] = React.useState(false);
  const [isVideoOff, setIsVideoOff] = React.useState(false);

  // Track update counter to trigger re-attachment when tracks are published/unpublished
  const [trackUpdate, setTrackUpdate] = React.useState(0);

  // Listen for track events so we re-attach when camera/mic are enabled after mount
  React.useEffect(() => {
    const handleTrackChange = () => setTrackUpdate((n) => n + 1);

    participant.on(ParticipantEvent.TrackPublished, handleTrackChange);
    participant.on(ParticipantEvent.TrackUnpublished, handleTrackChange);
    participant.on(ParticipantEvent.TrackSubscribed, handleTrackChange);
    participant.on(ParticipantEvent.TrackUnsubscribed, handleTrackChange);
    participant.on(ParticipantEvent.TrackMuted, handleTrackChange);
    participant.on(ParticipantEvent.TrackUnmuted, handleTrackChange);
    participant.on(ParticipantEvent.LocalTrackPublished, handleTrackChange);
    participant.on(ParticipantEvent.LocalTrackUnpublished, handleTrackChange);

    return () => {
      participant.off(ParticipantEvent.TrackPublished, handleTrackChange);
      participant.off(ParticipantEvent.TrackUnpublished, handleTrackChange);
      participant.off(ParticipantEvent.TrackSubscribed, handleTrackChange);
      participant.off(ParticipantEvent.TrackUnsubscribed, handleTrackChange);
      participant.off(ParticipantEvent.TrackMuted, handleTrackChange);
      participant.off(ParticipantEvent.TrackUnmuted, handleTrackChange);
      participant.off(ParticipantEvent.LocalTrackPublished, handleTrackChange);
      participant.off(ParticipantEvent.LocalTrackUnpublished, handleTrackChange);
    };
  }, [participant]);

  // Attach video track to element
  React.useEffect(() => {
    const videoTrack = participant.getTrackPublication(Track.Source.Camera);
    const audioTrack = participant.getTrackPublication(Track.Source.Microphone);

    if (videoTrack?.track && videoRef.current) {
      videoTrack.track.attach(videoRef.current);
    }

    // Attach microphone track to a hidden <audio> element.
    // LiveKit does not auto-play subscribed audio unless it is attached.
    if (audioTrack?.track && audioRef.current) {
      audioTrack.track.attach(audioRef.current);
    }

    // Update mute states
    // Use .track existence instead of .isSubscribed (isSubscribed is undefined for local participants)
    setIsMuted(!audioTrack?.track || audioTrack?.isMuted || false);
    setIsVideoOff(!videoTrack?.track || videoTrack?.isMuted || false);

    return () => {
      videoTrack?.track?.detach();
      audioTrack?.track?.detach();
    };
  }, [participant, trackUpdate]);

  // Get participant name from JWT name field, metadata, or identity
  const getName = () => {
    if (participant.name) return participant.name;
    try {
      const metadata = JSON.parse(participant.metadata || "{}");
      return metadata.name || participant.identity;
    } catch {
      return participant.identity;
    }
  };

  // Get connection quality indicator
  const getQualityIndicator = () => {
    const quality = participant.connectionQuality;
    const dots = quality === "excellent" ? 3 : quality === "good" ? 2 : 1;
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className={cn(
              "w-1.5 h-1.5 rounded-full",
              i <= dots ? "bg-green-500" : "bg-stone-400"
            )}
          />
        ))}
      </div>
    );
  };

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-xl bg-stone-900",
        isSpotlight ? "aspect-video" : "aspect-video",
        className
      )}
    >
      {/* Video element - always rendered so ref is available for track attachment */}
      {/* Live video has no captions track; captions are not available in this context. */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className={cn(
          "absolute inset-0 w-full h-full",
          isSpotlight ? "object-contain" : "object-cover",
          isVideoOff && "hidden"
        )}
      />

      {/* Audio element for remote microphone playback */}
      {/* Remote audio playback has no captions track; captions are not available in this context. */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio ref={audioRef} autoPlay playsInline className="hidden" />

      {/* Avatar fallback when camera is off */}
      {isVideoOff && (
        <div className="absolute inset-0 flex items-center justify-center bg-stone-800">
          <div className="flex flex-col items-center gap-2">
            <div className="w-16 h-16 rounded-full bg-sage-600 flex items-center justify-center text-white text-2xl font-semibold">
              {getName().charAt(0).toUpperCase()}
            </div>
            <span className="text-stone-400 text-sm">Kamera aus</span>
          </div>
        </div>
      )}

      {/* Overlay with name and indicators */}
      {showName && (
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-white text-sm font-medium truncate max-w-[150px]">
                {getName()}
              </span>
              {isMuted && (
                <MicOff className="w-4 h-4 text-red-400" />
              )}
            </div>
            {getQualityIndicator()}
          </div>
        </div>
      )}
    </div>
  );
}
